import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';

import { Figure, Flow } from '../components/Diagram';

const DIRECT_SUBMISSION = `// POST https://api.revvly.ai/api/v1/graphql
mutation CreateProspect($input: CreateProspectInput!) {
  createProspect(input: $input) {
    organization { id name domain }
    contact { id firstName lastName email }
    lead { id title externalRef pipelineId pipelineStageId }
  }
}

# variables
{
  "input": {
    "organization": {
      "name": "Northstar Labs",
      "domain": "northstarlabs.example",
      "industry": "Software"
    },
    "contact": {
      "firstName": "Avery",
      "lastName": "Chen",
      "email": "avery@northstarlabs.example"
    },
    "lead": {
      "title": "Enterprise demo request",
      "externalRef": "form-submission-evt_01J8K3",
      "estimatedValue": 24000,
      "currency": "USD"
    }
  }
}`;

const SERVER_HANDLER = `export async function submitToRevvly(formEvent: {
  id: string;
  company: string;
  website?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}) {
  const response = await fetch('https://api.revvly.ai/api/v1/graphql', {
    method: 'POST',
    headers: {
      Authorization: \`Bearer \${process.env.REVVLY_TOKEN}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: \`mutation CreateProspect($input: CreateProspectInput!) {
        createProspect(input: $input) {
          organization { id name }
          contact { id email }
          lead { id externalRef }
        }
      }\`,
      variables: {
        input: {
          organization: { name: formEvent.company, domain: formEvent.website },
          contact: {
            firstName: formEvent.firstName,
            lastName: formEvent.lastName,
            email: formEvent.email,
          },
          lead: {
            title: 'Website demo request',
            externalRef: formEvent.id,
          },
        },
      },
    }),
  });

  const result = await response.json();
  if (result.errors) throw new Error(result.errors[0].message);
  return result.data.createProspect;
}`;

const RETRY = `// Keep the same externalRef if the provider retries this event.
const prospect = await submitToRevvly({
  id: 'form-submission-evt_01J8K3',
  company: 'Northstar Labs',
  email: 'avery@northstarlabs.example',
});

// A completed previous delivery returns the same prospect, contact, and lead.
console.log(prospect.lead.externalRef);`;

const AGENT_PROMPT = buildIntegrationPrompt('prospectForm');

export function ProspectFormIntegrationGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Send form prospects to Revvly — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Prospect form integration</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Turn a form event into a <em>pipeline-ready prospect</em>.
        </h1>
        <p className="dp-oneliner">
          Send one public GraphQL mutation from your form webhook to create the prospect company,
          its primary contact, and the deal your team will work in Revvly.
        </p>
      </header>

      <section className="dp-section">
        <h2>Before you start</h2>
        <p className="dp-plain">
          Enable CRM for the tenant and issue an API key with the WRITE capability. Keep that key on
          your server or webhook worker only; a browser form must submit to your backend, not
          directly to Revvly. The public API is at{' '}
          <span className="api-inline">https://api.revvly.ai/api/v1/graphql</span>.
        </p>
      </section>

      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <p className="dp-plain">
          Copy this context into another coding or automation agent so it can build the integration
          without confusing a CRM prospect with a billing Client.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      <section className="dp-section">
        <h2>Example 1: map a standard lead form</h2>
        <p className="dp-plain">
          Map the company to <span className="api-inline">organization</span>, the person to{' '}
          <span className="api-inline">contact</span>, and the form intent to{' '}
          <span className="api-inline">lead</span>. A lead title and the provider&rsquo;s submission
          id are required.
        </p>
        <pre className="api-code">{DIRECT_SUBMISSION}</pre>
      </section>

      <section className="dp-section">
        <h2>Example 2: forward a webhook from your server</h2>
        <p className="dp-plain">
          This server-side example takes a normalized form event and forwards only the fields
          supported by the public contract. Add your own webhook signature verification before this
          handler runs.
        </p>
        <Figure
          title="From form event to prospect"
          caption="Your server sits between the form and Revvly, and the submission id is what makes a retry safe."
        >
          <Flow
            steps={[
              { title: 'Form is submitted', text: 'Your form provider sends a webhook.' },
              {
                title: 'Your server',
                text: 'Verifies the webhook and holds the API key with WRITE.',
              },
              {
                title: 'createProspect',
                text: 'One public GraphQL mutation, with the submission id as externalRef.',
                tone: 'accent',
              },
              {
                fork: [
                  {
                    title: 'New prospect',
                    text: 'Creates the company, contact and lead, in the default pipeline.',
                    tone: 'accent',
                  },
                  {
                    title: 'Event delivered again',
                    text: 'Returns the records already created.',
                    tone: 'muted',
                  },
                  {
                    title: 'Domain or email taken',
                    text: 'Returns CONFLICT.',
                    tone: 'warn',
                  },
                ],
              },
            ]}
          />
        </Figure>
        <pre className="api-code">{SERVER_HANDLER}</pre>
      </section>

      <section className="dp-section">
        <h2>Example 3: retry safely</h2>
        <p className="dp-plain">
          Use an immutable provider event or submission id for{' '}
          <span className="api-inline">lead.externalRef</span>. Do not generate a new value on every
          delivery. If a completed event is delivered again, Revvly returns the previously created
          records instead of duplicating the prospect.
        </p>
        <pre className="api-code">{RETRY}</pre>
      </section>

      <section className="dp-section">
        <h2>Defaults and validation</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Pipeline placement</div>
            <div className="dp-field-v">
              Omit pipeline and stage to use the tenant&rsquo;s default pipeline and its first Open
              stage. Supply ids only when the form belongs in a specific pipeline.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Duplicates</div>
            <div className="dp-field-v">
              Organization domains and contact email addresses are unique per tenant. A duplicate
              returns <span className="api-inline">CONFLICT</span>; use the provider submission id
              for retries, not as a way to merge separately submitted leads.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Custom form fields</div>
            <div className="dp-field-v">
              Revvly stores reserved custom-field data internally, but custom fields are not a
              supported public API contract yet. Send only organization, contact, and lead fields
              documented in the API reference.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link
              to="/docs/api/graphql-public"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Public GraphQL API reference ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/crm" style={{ color: 'inherit', textDecoration: 'none' }}>
              Run your sales pipeline ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
